import { SeoPageShell } from '../components/SeoPageShell';

// ==================================================================
// /affordable-grant-search-tool  (PRIMARY KEYWORD PAGE)
// ==================================================================
export function AffordableGrantSearchTool() {
  return (
    <SeoPageShell
      title="Affordable Grant Search Tool for Small Nonprofits ($19/mo)"
      description="RaiseRoot is the most affordable grant search tool built for small nonprofits — starting at $19/month with a 7-day free trial. Same-quality database as $200/mo tools."
      path="/affordable-grant-search-tool"
      h1="The affordable grant search tool small nonprofits actually deserve."
      subhead="Grant discovery, deadline tracking, and corporate partnership matching starting at $19/month. Same-quality database, priced honestly. 7-day free trial — no charge for 7 days."
      sections={[
        {
          h2: 'Why grant search tools are so expensive (and why they don\'t need to be)',
          paragraphs: [
            'Most grant discovery platforms — Instrumentl, GrantStation, Foundation Directory — charge between $200 and $500 per month. That pricing was designed for large nonprofits with dedicated development departments and enterprise budgets.',
            'For a small nonprofit with a $200,000 annual budget, spending $2,400+/year just to search for grants is often a non-starter. So small orgs either pay up and squeeze other line items, or give up on grant search tools entirely and rely on free Google searches — missing hundreds of relevant opportunities every year.',
            'RaiseRoot was built specifically for that gap. We license the same-quality grant data as the big players, but we removed the enterprise sales team, the annual contract, and the bloated feature set nobody uses. The result: $19/month for the Starter plan.',
          ],
        },
        {
          h2: 'What\'s included in RaiseRoot at $19/month',
          paragraphs: ['Everything a small nonprofit actually needs to find and win grants:'],
          bullets: [
            '25 filtered grant searches per month (Starter) or 50/month (Growth $39)',
            '26+ search filters: focus area, funding amount, geography, funder type, population served',
            'Save unlimited grants to your watchlist — no per-item fees',
            'Deadline calendar with automatic reminders',
            'Grant application tracker with status pipeline (Draft → Submitted → Awarded)',
            'Auto-generated Letters of Intent (LOI) to jumpstart proposals',
            'Corporate Partnership Builder — no separate tool required',
            'Learning hub with grant writing templates and training articles',
          ],
        },
        {
          h2: 'How to know if you\'re paying too much for grant search',
          paragraphs: [
            'If you\'re paying more than $50/month for grant search, you\'re likely overpaying. Here\'s a quick self-audit:',
          ],
          bullets: [
            'Are you locked into an annual contract? Small nonprofits should never sign 12-month grant tool contracts.',
            'Are you paying for features you don\'t use — CRM integrations, donor scoring models, corporate DEI dashboards?',
            'Is the tool asking you to schedule a "demo" instead of showing pricing? That\'s enterprise sales — expect $300+/month.',
            'Does the tool exclude small foundations under $50K/year in giving? Legacy tools often skip these — and small foundations are exactly where small nonprofits win.',
          ],
        },
        {
          h2: 'Grant search + corporate partnerships in one place',
          paragraphs: [
            'Most legacy grant tools focus only on foundation and government grants. But corporate sponsorships often make up 30-50% of a small nonprofit\'s funding — and they\'re typically the hardest to find without a research team.',
            'RaiseRoot bundles both. Every plan includes the Corporate Partnership Builder — a guided pipeline that suggests local companies based on your mission, drafts outreach emails, and tracks partnerships from identify to won. No separate tool. No extra fee.',
          ],
        },
      ]}
      faqs={[
        { q: 'Is $19/month really enough to run a good grant search tool?', a: 'Yes. We keep pricing low by using a lean team, no enterprise sales, and modern infrastructure. The grant data is licensed from the same providers as tools charging 10x more.' },
        { q: 'How does the 7-day free trial work?', a: 'Sign up with a card, use everything for 7 full days. If it doesn\'t help you find funding, cancel before day 8 and pay nothing. On day 8, you\'re billed the monthly rate.' },
        { q: 'What\'s a "grant search"?', a: 'Each filtered query — by keyword, focus area, amount, geography — counts as one search. Viewing saved grants, opening details, and running the tracker are all unlimited.' },
        { q: 'Do you have a plan for larger nonprofits?', a: 'Growth ($39/mo) covers most small-to-mid organizations with 50 searches and a monthly strategy session. For orgs needing more, contact us for a custom quote.' },
        { q: 'Do you offer a free tier?', a: 'No. We removed the free tier because it created friction and low-quality users. Instead, we offer a 7-day free trial with full access — you get to try the real product, not a limited demo.' },
      ]}
    />
  );
}


// ==================================================================
// /instrumentl-alternative
// ==================================================================
export function InstrumentlAlternative() {
  return (
    <SeoPageShell
      title="Instrumentl Alternative for Small Nonprofits — $19/mo"
      description="Looking for an Instrumentl alternative? RaiseRoot offers the same grant discovery + corporate partnerships at $19/month vs $179+/month. 7-day free trial."
      path="/instrumentl-alternative"
      h1="The Instrumentl alternative built for small nonprofits."
      subhead="Same grant matching, deadline tracking, and pipeline — priced 10x lower. RaiseRoot starts at $19/month with a 7-day free trial, no annual contract, and a built-in corporate partnership pipeline Instrumentl doesn't offer."
      comparison={{
        title: 'RaiseRoot vs Instrumentl',
        competitors: ['Instrumentl'],
        rows: [
          { feature: 'Monthly price', competitors: ['$179+/mo'], raiseroot: '$19/mo' },
          { feature: 'Annual contract required', competitors: [true], raiseroot: false },
          { feature: 'Grant search & matching', competitors: [true], raiseroot: true },
          { feature: 'Deadline tracking', competitors: [true], raiseroot: true },
          { feature: 'Watchlist / saved grants', competitors: [true], raiseroot: true },
          { feature: 'Corporate partnership pipeline', competitors: [false], raiseroot: true },
          { feature: 'Auto-generated Letters of Intent', competitors: [false], raiseroot: true },
          { feature: 'Strategy session included', competitors: [false], raiseroot: 'Yes ($39 plan)' },
          { feature: '7-day free trial (full access)', competitors: ['Limited'], raiseroot: true },
        ],
      }}
      sections={[
        {
          h2: 'Why small nonprofits switch from Instrumentl',
          paragraphs: [
            'Instrumentl is a solid tool — but it was built for mid-sized and larger nonprofits with dedicated grant writers. For small nonprofits (annual budget under $500K), three things typically drive the switch to RaiseRoot:',
          ],
          bullets: [
            'Price: $179/mo minimum vs $19/mo. Over a year, that\'s $2,148 vs $228 — a $1,920 difference that pays for a program.',
            'Annual contract: Instrumentl requires 12-month commitments. RaiseRoot is month-to-month.',
            'No corporate partnerships: Instrumentl doesn\'t help you find corporate sponsors. RaiseRoot includes a full Corporate Partnership Builder at every plan tier.',
          ],
        },
        {
          h2: 'What you gain (and don\'t lose) with RaiseRoot',
          paragraphs: [
            'Same-quality grant discovery: search by focus area, funding range, geography, and 20+ other filters. Same deadline calendar. Same watchlist. Same application tracker.',
            'What you gain: corporate partnership matching, monthly strategy sessions (Growth plan), and honest pricing that scales with your budget, not your compliance team.',
            'What you don\'t lose: any grant data. We license from the same underlying providers and update opportunities daily.',
          ],
        },
        {
          h2: 'Is RaiseRoot the right Instrumentl alternative for you?',
          paragraphs: [
            'RaiseRoot is the right fit if:',
          ],
          bullets: [
            'Your annual budget is under $2M and grant tools eat too much of it',
            'You want to combine grant search + corporate partnership research in one tool',
            'You prefer month-to-month over annual contracts',
            'You want a free 7-day trial before committing (not just a sales demo)',
            'You don\'t need enterprise-only features like custom dashboards or CRM integrations',
          ],
        },
      ]}
      faqs={[
        { q: 'How does RaiseRoot compare to Instrumentl on data quality?', a: 'We license the same-quality grant data providers as Instrumentl and refresh opportunities daily. The core database is comparable.' },
        { q: 'Can I import my Instrumentl watchlist?', a: 'Not yet, but you can bulk-add grants manually in the tracker. Contact us if you have more than 50 grants to migrate — we\'ll help.' },
        { q: 'Does RaiseRoot have a free tier like Instrumentl trial?', a: 'We offer a 7-day free trial with full access. On day 8 you\'re billed unless you cancel. No permanently-free tier.' },
        { q: 'Why is RaiseRoot so much cheaper than Instrumentl?', a: 'We removed enterprise sales, annual contracts, and features large nonprofits use (custom reports, CRM integrations). What\'s left is exactly what small nonprofits need.' },
      ]}
    />
  );
}


// ==================================================================
// /grantstation-alternative
// ==================================================================
export function GrantstationAlternative() {
  return (
    <SeoPageShell
      title="GrantStation Alternative for Small Nonprofits — $19/mo"
      description="Looking for a GrantStation alternative? RaiseRoot offers modern grant search + corporate partnerships at $19/mo with a 7-day trial. No annual contract."
      path="/grantstation-alternative"
      h1="A GrantStation alternative built for the modern small nonprofit."
      subhead="GrantStation has been around since 1998 — RaiseRoot is what grant search looks like in 2026. Cleaner filters, corporate partnership pipeline included, $19/month starting price, and a 7-day free trial you can actually try."
      comparison={{
        title: 'RaiseRoot vs GrantStation',
        competitors: ['GrantStation'],
        rows: [
          { feature: 'Monthly price', competitors: ['~$79/mo (annual only)'], raiseroot: '$19/mo' },
          { feature: 'Annual contract required', competitors: [true], raiseroot: false },
          { feature: 'Modern UI', competitors: [false], raiseroot: true },
          { feature: 'Corporate partnership pipeline', competitors: [false], raiseroot: true },
          { feature: 'Auto-generated Letters of Intent', competitors: [false], raiseroot: true },
          { feature: 'Deadline calendar', competitors: [true], raiseroot: true },
          { feature: 'Strategy session included', competitors: [false], raiseroot: 'Yes ($39 plan)' },
          { feature: '7-day free trial', competitors: [false], raiseroot: true },
        ],
      }}
      sections={[
        {
          h2: 'Why nonprofits switch from GrantStation',
          paragraphs: [
            'GrantStation has one of the largest funder databases in the space. But two factors drive small nonprofits to look for alternatives:',
          ],
          bullets: [
            'Annual-only pricing at ~$79/month billed upfront (~$949 for the year). That\'s $721 more per year than RaiseRoot Starter.',
            'The interface hasn\'t been meaningfully updated in years. Search filters, results pages, and tracker views feel dated compared to modern SaaS tools.',
            'No corporate partnership pipeline. You\'ll need a second tool (or a manual spreadsheet) to research and manage corporate sponsors.',
          ],
        },
        {
          h2: 'What RaiseRoot does differently',
          paragraphs: [
            'RaiseRoot combines grant discovery + corporate partnership research into a single monthly subscription. You get:',
          ],
          bullets: [
            '25-50 monthly grant searches with modern, fast filtering (26+ criteria)',
            'Full corporate partnership pipeline: identify → research → pitch → won',
            'Auto-generated Letters of Intent to jumpstart proposals',
            'A monthly 1:1 strategy session on the Growth plan',
            'No annual lock-in — cancel anytime',
          ],
        },
      ]}
      faqs={[
        { q: 'Is RaiseRoot as comprehensive as GrantStation?', a: 'For the funding types small nonprofits pursue — foundation grants, government grants, and corporate giving — yes. We may not cover the most niche international funders, but 95%+ of small-nonprofit opportunities are in our database.' },
        { q: 'Can I cancel a RaiseRoot subscription at any time?', a: 'Yes. Cancel from your account settings and you keep access through the end of your billing month. No cancellation fees.' },
        { q: 'Does RaiseRoot offer state-specific grants like GrantStation?', a: 'Yes. Filter by state, region, or metro area during search.' },
      ]}
    />
  );
}


// ==================================================================
// /how-to-find-corporate-sponsors-for-nonprofits
// ==================================================================
export function HowToFindCorporateSponsors() {
  return (
    <SeoPageShell
      title="How to Find Corporate Sponsors for Your Nonprofit (2026 Guide)"
      description="Complete guide to finding corporate sponsors for small nonprofits. Where to look, how to pitch, and a free 7-day trial of the tool that automates it."
      path="/how-to-find-corporate-sponsors-for-nonprofits"
      h1="How to find corporate sponsors for your nonprofit."
      subhead="A step-by-step 2026 guide for small nonprofits — plus the tool that automates the research and outreach in minutes instead of weeks."
      cta="Try the Partnership Builder Free"
      sections={[
        {
          h2: 'Step 1: Start with companies that already care about your cause',
          paragraphs: [
            'Corporate sponsorship isn\'t random — it\'s mission alignment. The best sponsor for a youth literacy nonprofit isn\'t Fortune 500 — it\'s the local bookstore, the regional print shop, or the tech company whose CEO went to a Title I school.',
            'Start with these 4 sources for warm leads:',
          ],
          bullets: [
            'Your board members\' employers — every board member is a potential warm intro to their company\'s CSR program',
            'Peer nonprofits\' sponsor lists — check their annual reports and event programs, then approach the same companies',
            'Local Chamber of Commerce membership directories — filter for companies over 20 employees in your metro area',
            'Companies whose CSR pages mention your cause area (education, environment, health, arts, etc.)',
          ],
        },
        {
          h2: 'Step 2: Research each prospect before you pitch',
          paragraphs: [
            'The #1 reason corporate outreach fails: nonprofits send generic pitches to the wrong contact. Before you send a single email, spend 15 minutes on each prospect researching:',
          ],
          bullets: [
            'Their giving history — do they sponsor events, provide product donations, or run employee matching programs?',
            'Their CSR priorities — most mid-size companies publish an annual "community giving" or "impact" report',
            'The right contact — CSR Manager, Community Relations Director, or (in smaller companies) the CEO or Marketing VP',
            'Recent news — mergers, layoffs, or new product launches change their giving capacity',
          ],
        },
        {
          h2: 'Step 3: Pitch the value exchange, not the ask',
          paragraphs: [
            'Corporate sponsorship is a business transaction. Companies don\'t give money to be nice — they give money because it drives brand awareness, employee engagement, or community goodwill that\'s tied to their strategy.',
            'A winning pitch email includes:',
          ],
          bullets: [
            'One sentence connecting their business to your mission (specific, not generic)',
            'One concrete "here\'s what your logo/team gets" benefit (event visibility, employee volunteer day, social media features)',
            'One ask with a range — "$2,500 sponsorship or in-kind support of similar value"',
            'A specific next step — "15-minute call next Tuesday" not "let me know if interested"',
          ],
        },
        {
          h2: 'Step 4: Track everything (or lose the deal)',
          paragraphs: [
            'The average corporate sponsorship takes 4-8 weeks and 3-5 touchpoints from first email to signed agreement. Without a tracker, you\'ll forget who\'s at which stage — and lose warm prospects.',
            'You need a pipeline that shows:',
          ],
          bullets: [
            'Identified — you\'ve added the company to your list',
            'Researched — you\'ve confirmed CSR fit and identified the right contact',
            'Contacted — first outreach sent',
            'Meeting Set — they\'ve agreed to a call',
            'Proposal Sent — formal ask with dollar amounts',
            'Partnered — signed agreement or committed sponsorship',
          ],
        },
        {
          h2: 'How RaiseRoot\'s Corporate Partnership Builder automates all of this',
          paragraphs: [
            'RaiseRoot includes a Corporate Partnership Builder at every plan tier ($19/mo Starter, $39/mo Growth). It handles the four steps above automatically:',
          ],
          bullets: [
            'Smart-matched company suggestions based on your mission and location',
            'Pre-researched CSR fit indicators (giving history, priorities, difficulty level)',
            'Auto-drafted outreach emails you can send in one click',
            'Built-in 6-stage pipeline tracker with next-action prompts',
            '7-day free trial — no credit card charged for 7 days',
          ],
        },
      ]}
      faqs={[
        { q: 'How much money can small nonprofits realistically raise from corporate sponsors?', a: 'For a nonprofit with a $200-500K annual budget, $10-50K/year in corporate sponsorships is achievable in year one, growing to $50-150K by year three with a repeatable pipeline.' },
        { q: 'What\'s the biggest mistake small nonprofits make with corporate outreach?', a: 'Sending generic templates to CSR@company.com email addresses. Personalized outreach to a named contact gets 10x higher response rates.' },
        { q: 'How long does it take to close a corporate sponsorship?', a: 'Typically 4-8 weeks from first email to signed agreement. Smaller local companies close faster (~2-4 weeks); Fortune 500 CSR programs can take 3-6 months.' },
        { q: 'Do I need a formal 501(c)(3) status to get corporate sponsorships?', a: 'For most sponsorships yes — but some companies sponsor fiscally-sponsored projects too. If you\'re pre-501(c)(3), highlight your fiscal sponsor.' },
      ]}
    />
  );
}
